import { useState } from 'react';
import { ChevronDown, ChevronRight, FileText } from 'lucide-react';

import { CARGOS, numero, percentual, reais } from '../lib/formato.ts';
import type { LinhaResultado } from '../lib/tipos.ts';
import { Etiqueta, Tabela } from './ui.tsx';

function tomDoBalizador(rotulo: string): string {
  if (rotulo.startsWith('ACIMA')) return 'bom';
  if (rotulo.startsWith('NA MÉDIA')) return 'medio';
  if (rotulo.startsWith('NÃO APLIC')) return 'neutro';
  return 'ruim';
}

/**
 * A tabela de resultado, usada tanto na prévia quanto num mês já fechado.
 * Clicar no nome abre o memorial — as frases que explicam como se chegou
 * naquele valor, que é o que responde uma contestação.
 */
export function TabelaResultado({
  linhas,
  onComprovante,
}: {
  linhas: LinhaResultado[];
  onComprovante?: (linha: LinhaResultado) => void;
}) {
  const [aberta, setAberta] = useState<string | null>(null);
  const total = linhas.reduce((s, l) => s + l.totalFinal, 0);
  const colunas = onComprovante === undefined ? 11 : 12;

  return (
    <Tabela
      cabecalho={
        <tr>
          <th className="py-2 pr-3">Nome</th>
          <th className="py-2 pr-3">Cargo</th>
          <th className="py-2 pr-3 text-right">Agend.</th>
          <th className="py-2 pr-3 text-right">Atend.</th>
          <th className="py-2 pr-3 text-right">Taxa</th>
          <th className="py-2 pr-3">Fase</th>
          <th className="py-2 pr-3 text-right">Ocupação</th>
          <th className="py-2 pr-3 text-right">Compar.</th>
          <th className="py-2 pr-3">Balizador</th>
          <th className="py-2 pr-3 text-right">Fator</th>
          <th className="py-2 pr-3 text-right">Total</th>
          {onComprovante !== undefined && <th className="py-2" />}
        </tr>
      }
    >
      {linhas.map((l) => [
        <tr key={l.nome} className="hover:bg-slate-50">
          <td className="py-2 pr-3">
            <button
              onClick={() => setAberta(aberta === l.nome ? null : l.nome)}
              className="inline-flex items-center gap-1 text-left hover:underline"
            >
              {aberta === l.nome ? (
                <ChevronDown className="size-3.5 shrink-0" />
              ) : (
                <ChevronRight className="size-3.5 shrink-0" />
              )}
              {l.nome}
            </button>
          </td>
          <td className="py-2 pr-3 text-slate-600">{CARGOS[l.cargoCodigo] ?? l.cargoCodigo}</td>
          <td className="num py-2 pr-3">{numero(l.compAgendado)}</td>
          <td className="num py-2 pr-3">{numero(l.compAtendido)}</td>
          <td className="num py-2 pr-3">{percentual(l.compTaxa)}</td>
          <td className="py-2 pr-3 whitespace-nowrap text-slate-600">{l.compFase}</td>
          <td className="num py-2 pr-3">{reais(l.ocupacaoValor)}</td>
          <td className="num py-2 pr-3">{reais(l.compValor)}</td>
          <td className="py-2 pr-3">
            <Etiqueta tom={tomDoBalizador(l.balizRotulo)}>{l.balizRotulo}</Etiqueta>
          </td>
          <td className="num py-2 pr-3">{percentual(l.balizFator)}</td>
          <td className="num py-2 pr-3 font-semibold">{reais(l.totalFinal)}</td>
          {onComprovante !== undefined && (
            <td className="py-2">
              <button
                onClick={() => onComprovante(l)}
                title={`Comprovante de ${l.nome}`}
                className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-900"
              >
                <FileText className="size-4" />
              </button>
            </td>
          )}
        </tr>,
        aberta === l.nome ? (
          <tr key={`${l.nome}-memorial`} className="bg-slate-50">
            <td colSpan={colunas} className="px-3 py-3">
              <p className="mb-2 text-xs font-medium tracking-wide text-slate-500 uppercase">
                Como se chegou a este valor
              </p>
              <ol className="space-y-1 text-sm text-slate-700">
                {l.memorial.map((m, i) => (
                  <li key={i}>· {m}</li>
                ))}
              </ol>
            </td>
          </tr>
        ) : null,
      ])}
      <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold">
        <td className="py-3 pr-3" colSpan={10}>
          Total geral
        </td>
        <td className="num py-3 pr-3">{reais(total)}</td>
        {onComprovante !== undefined && <td />}
      </tr>
    </Tabela>
  );
}
